<template>
    <ModalShell title="Add Family Member" subtitle="Who would you like to add to your protection plan?" footer-class="pt-6">
        <template #icon>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H6C4.93913 15 3.92172 15.4214 3.17157 16.1716C2.42143 16.9217 2 17.9391 2 19V21M19 8V14M22 11H16M13 7C13 9.20914 11.2091 11 9 11C6.79086 11 5 9.20914 5 7C5 4.79086 6.79086 3 9 3C11.2091 3 13 4.79086 13 7Z" stroke="#181D27" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        </template>
        <!-- Error message for ineligible users -->
        <div v-if="errorMessage" class="error-banner">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p>{{ errorMessage }}</p>
        </div>

        <!-- Selection options -->
        <div v-if="!isLoading" class="options-section">
            <button class="option-card" @click="selectAdult">
                <div class="option-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                    </svg>
                </div>
                <div class="option-text">
                    <span class="option-title">Protect Another Adult</span>
                    <span class="option-desc">Add an adult family member to your plan</span>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="chevron">
                    <polyline points="9 18 15 12 9 6" />
                </svg>
            </button>

            <button class="option-card" @click="selectChild">
                <div class="option-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="4.5" r="2.5" /><path d="m10.2 15.5-1.7 4.7a.7.7 0 0 0 .7.8h5.6a.7.7 0 0 0 .7-.8l-1.7-4.7" /><path d="M15.5 10a3.5 3.5 0 1 0-7 0v5.5h7z" />
                    </svg>
                </div>
                <div class="option-text">
                    <span class="option-title">Protect Your Child</span>
                    <span class="option-desc">Add a minor under 18 to your plan</span>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="chevron">
                    <polyline points="9 18 15 12 9 6" />
                </svg>
            </button>
        </div>

        <!-- Loading state -->
        <div v-if="isLoading" class="loading-section">
            <i class="pi pi-spin pi-spinner" style="font-size: 1.5rem"></i>
            <p>Checking eligibility...</p>
        </div>
        <template #footer>
            <PrimeButton
                label="Cancel"
                class="p-btn-root_secondary"
                @click="closeDialog"
            />
        </template>
    </ModalShell>
</template>

<script setup lang="ts">
import ModalShell from '~/components/modals/shared/ModalShell.vue';
import { useContactsStore } from '~/stores/person/contacts';
import { ModalNames } from '~/types/modals';

const contactsStore = useContactsStore();
const dialogRef = getDialogRef(ModalNames.ADD_MEMBER_TYPE_SELECT);

const isLoading = ref(false);
const errorMessage = ref('');

onMounted(() => {
    if (window.innerWidth < 1024) {
        document.body.classList.add('pb-scroll-lock');
        document.documentElement.classList.add('pb-scroll-lock');
    }
});

onUnmounted(() => {
    document.body.classList.remove('pb-scroll-lock');
    document.documentElement.classList.remove('pb-scroll-lock');
});

const closeDialog = () => {
    dialogRef.value.close();
};

const selectAdult = () => {
    closeDialog();
    useDynamicDialog(ModalNames.ADD_CONTACT);
};

const selectChild = async () => {
    isLoading.value = true;
    errorMessage.value = '';

    try {
        const result = await contactsStore.checkMinorEligibility();

        if (result.eligible) {
            closeDialog();
            if (result.consent_signed) {
                useDynamicDialog(ModalNames.ADD_CHILD_FORM);
            } else {
                useDynamicDialog(ModalNames.ADD_CHILD_CONSENT);
            }
        } else {
            errorMessage.value = result.reason || 'Adding a minor to your account requires identity verification. You must either have an active paid license (verifying adult identity via payment) or be logged in through your employer\'s Single Sign-On (SSO). Please upgrade your account or contact your employer.';
        }
    } catch {
        errorMessage.value = 'Unable to check eligibility. Please try again.';
    } finally {
        isLoading.value = false;
    }
};
</script>

<style lang="scss" scoped>
.error-banner {
    @apply flex gap-2 items-start p-3 mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 w-full;

    svg { @apply flex-shrink-0 mt-0.5; }
    p { @apply text-sm leading-5; }
}

.options-section {
    @apply flex flex-col gap-3 w-full;
}

.option-card {
    @apply flex items-center gap-4 p-4 rounded-lg border border-zinc-200 w-full text-left
           hover:bg-zinc-50 hover:border-zinc-300 transition-colors cursor-pointer;
}

.option-icon {
    @apply flex w-10 h-10 justify-center items-center rounded-full bg-blue-50 text-blue-600 flex-shrink-0;
}

.option-text {
    @apply flex flex-col flex-1;
}

.option-title {
    @apply text-sm font-semibold text-zinc-900;
}

.option-desc {
    @apply text-xs text-zinc-500;
}

.chevron {
    @apply text-zinc-400 flex-shrink-0;
}

.loading-section {
    @apply flex flex-col items-center gap-2 py-8 text-zinc-500 w-full;
    p { @apply text-sm; }
}
</style>
